import { Card, Heading, Stack, Text, cx } from '../design/index.js';
import { WeightPlanChart } from '../charts/WeightPlanChart.js';
import { WeightTrajectoryChart } from '../charts/WeightTrajectoryChart.js';
import type { PlanAssessment, WeeklyPlanPoint, WeightTrajectories } from '@loggy/shared';
import { buildPlanReadout } from './planReadout.js';
import styles from './PlanAnalysis.module.css';

export interface PlanAnalysisProps {
  assessment: PlanAssessment;
  points: WeeklyPlanPoint[];
  /** Weight in kg across the goal's span. Absent when there is not enough to
   *  draw one — a goal with no baseline, say — and then only the weekly
   *  chart is shown rather than a placeholder beside it. */
  trajectories: WeightTrajectories | null;
  unit: string;
  precision: number;
  targetDate: string | null;
  goingDown: boolean;
  /** Shown only when there is no profile — the one thing the reader could
   *  add, and what it would buy them. Absent for someone reading a dashboard
   *  that is not theirs, who has no profile to add. */
  onAddProfile?: () => void;
}

/**
 * The second chart on a weight goal, and the analysis under it.
 *
 * Every sentence comes from `buildPlanReadout`, which is a pure function with
 * its own tests — nothing here composes prose. This renders what that
 * decided, and its own only judgement is which tone gets which accent.
 */
export function PlanAnalysis({
  assessment,
  points,
  trajectories,
  unit,
  precision,
  targetDate,
  goingDown,
  onAddProfile,
}: PlanAnalysisProps) {

  const readout = buildPlanReadout({ assessment, unit, precision, targetDate, goingDown });

  return (
    <Card className={styles.card} data-testid="plan-analysis">
      <Stack gap={4}>
        <Stack gap={1}>
          <Text size="xs" color="muted" className={styles.label}>
            Against your goal
          </Text>
          <Heading level={3} size="md" className={cx(styles.headline, styles[readout.tone === 'on-course' ? 'onCourse' : readout.tone])}>
            {readout.headline}
          </Heading>
        </Stack>

        {/* Side by side: the same goal asked two different ways. The weekly
            chart is in kg/WEEK and the trajectory in kg, so they cannot share
            an axis — which is exactly why they are two charts and not one.
            They stack on a narrow screen, where half of each would be
            unreadable. */}
        <div className={styles.charts}>
          <div className={styles.chart}>
            <Text size="xs" color="muted" className={styles.label}>
              Week by week
            </Text>
            <WeightPlanChart
              points={points}
              unit={unit}
              precision={precision}
              healthyRange={assessment.healthyRange}
              gradualRange={assessment.gradualRange}
              recentRateKgPerWeek={assessment.recentRateKgPerWeek}
              goingDown={goingDown}
            />
          </div>

          {trajectories && (
            <div className={styles.chart}>
              <Text size="xs" color="muted" className={styles.label}>
                Where it is heading
              </Text>
              {/* Every line has its own switch in the chart's own legend
                  now, so there is nothing for this screen to toggle. */}
              <WeightTrajectoryChart
                trajectories={trajectories}
                unit={unit}
                precision={precision}
              />
            </div>
          )}
        </div>

        {/* Tiles, not paragraphs. This was four blocks of prose with the
            figures embedded in them, and the one the owner actually wanted —
            what the goal comes to in calories — was a clause in the middle of
            the third. A figure that has to be read to be found is not
            presented. */}
        <div className={styles.stats}>
          {readout.stats.map((stat) => (
            <div
              key={stat.label}
              className={cx(
                styles.stat,
                stat.lead && styles.statLead,
                stat.tone && styles[`stat${stat.tone[0]!.toUpperCase()}${stat.tone.slice(1)}`],
              )}
              data-testid={stat.lead ? 'plan-stat-lead' : undefined}
            >
              <Text as="div" size="xs" color="muted" className={styles.label}>
                {stat.label}
              </Text>
              <div className={styles.statValue}>
                <Text as="span" family="figure" tabular className={styles.statFigure}>
                  {stat.value}
                </Text>
                {stat.unit && (
                  <Text as="span" size="sm" color="muted">
                    {stat.unit}
                  </Text>
                )}
              </div>
              {stat.detail && (
                <Text as="div" size="xs" color="secondary">
                  {stat.detail}
                </Text>
              )}
            </div>
          ))}
        </div>

        {/* Two levers, as lines to scan. Neither is recommended over the
            other — which of your own goals to bend is not this app's call. */}
        {readout.suggestion && (
          <div
            className={cx(
              styles.suggestion,
              styles[readout.tone === 'on-course' ? 'onCourse' : readout.tone],
            )}
            data-testid="plan-suggestion"
          >
            <Text as="div" size="xs" color="muted" className={styles.label}>
              {readout.suggestion.title}
            </Text>
            <ul className={styles.options}>
              {readout.suggestion.options.map((option) => (
                <li key={option}>
                  <Text as="span" size="sm">
                    {option}
                  </Text>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Offered where the gap actually shows, rather than left for the
            reader to discover in Settings — this is the moment the missing
            band is in front of them. A button, not a link, because it moves
            them somewhere rather than fetching anything. */}
        {onAddProfile && (
          <Text size="xs" color="muted">
            Add your height, age and activity level to see what pace your own
            numbers support.{' '}
            <button type="button" className={styles.inlineAction} onClick={onAddProfile}>
              Add them in Settings
            </button>
          </Text>
        )}

        {readout.caveat && (
          <Text size="xs" color="muted" data-testid="plan-caveat">
            {readout.caveat}
          </Text>
        )}
      </Stack>
    </Card>
  );
}
